import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import React, { useState, useEffect } from 'react';

import { useTranslation, useBoolean } from '@hooks/index';
import { validateFields } from '@utils/validation';
import CharactersRemaining from '@components/core/characters-remaining';
import ExpandableItem from '@components/core/expandable/item';
import TextField from '@components/core/text-field';

import './styles.scss';

interface ILabelItem {
    label: string;
    disabled: boolean;
    selected: boolean;
    enabled: boolean;
    value: string;
}

const SummaryDetailTemplate = ({
    onDisplayValuesChange,
    changeDetectability,
    changeActive,
    modelName,
    setName,
    active,
    includeDetectability,
    labels,
    isEvaluationModelUsed,
    errorNameRequired,
    nameExistError,
    clearNameExistError,
    disableSave,
}: {
    onDisplayValuesChange: Function;
    changeDetectability: Function;
    changeActive: Function;
    modelName: string;
    setName: Function;
    active: boolean;
    includeDetectability: boolean;
    labels: {
        likelihood: ILabelItem[];
        impact: ILabelItem[];
        detectability: ILabelItem[];
    };
    isEvaluationModelUsed: boolean;
    errorNameRequired: boolean;
    nameExistError: string;
    clearNameExistError: Function;
    disableSave: Function;
}) => {
    const initialState = {
        name: '',
        active: false,
        detectability: true,
        likelihoodLimit: 10,
        impactLimit: 10,
        detectabilityLimit: 10,
    };

    const createArray = (length: number | string) => {
        if (length === 0 || length === '-') return null;
        let array: any = Array.from(Array(length).keys());
        array = array.map((item: number, index: number) => ({
            label: `${index + 1}`,
            disabled: index === 0,
            selected: index === 0,
        }));
        return array;
    };

    const { t } = useTranslation();
    const disabledSave = useBoolean(true);
    const isFirst = useBoolean(true);

    const [state, setState] = useState(initialState);
    const [errors, setErrors] = useState({}) as any;
    const [arrayLikelihood, setArrayLikelihood] = useState(createArray(state.likelihoodLimit));
    const [arrayImpact, setArrayImpact] = useState(createArray(state.impactLimit));
    const [arrayDetectability, setArrayDetectability] = useState(createArray(state.detectabilityLimit));

    useEffect(() => {
        if (labels && isFirst.value) {
            if (labels.likelihood && labels.likelihood.length) {
                const arraylikelihood = [...labels.likelihood];
                setArrayLikelihood(
                    arraylikelihood.map((item: ILabelItem, index: number) => ({
                        label: `${item.value}`,
                        disabled: index === 0,
                        selected: item.enabled,
                    })),
                );
            }
            if (labels.impact && labels.impact.length) {
                const arrayimpact = [...labels.impact];
                setArrayImpact(
                    arrayimpact.map((item: ILabelItem, index: number) => ({
                        label: `${item.value}`,
                        disabled: index === 0,
                        selected: item.enabled,
                    })),
                );
            }
            if (labels.detectability && labels.detectability.length) {
                const arraydetectability = [...labels.detectability];
                if (arraydetectability?.length)
                    setArrayDetectability(
                        arraydetectability.map((item: ILabelItem, index: number) => ({
                            label: `${item.value}`,
                            disabled: index === 0,
                            selected: item.enabled,
                        })),
                    );
            }
            isFirst.setFalse();
        }
    }, [labels]);

    useEffect(() => {
        if (modelName.length && !state.name.length) {
            setState({
                ...state,
                name: modelName,
            });
        }
    }, [modelName]);

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);
        setErrors({
            ...errors,
            ...error,
        });
    };

    useEffect(() => {
        if (errors.name) disableSave();
    }, [errors.name]);

    useEffect(() => {
        if (errorNameRequired) validate(state.name, 'name', 70, true);
    }, [errorNameRequired]);

    const onChange = (name: string) => (event: any) => {
        if (name === 'name') {
            clearNameExistError();
            setName(event.target.value);
        }
        disabledSave.setFalse();
        const length = 70;

        setState({
            ...state,
            [name]: event.target.value,
        });
        validate(event.target.value, name, length, false);
    };

    return (
        <ExpandableItem header={t('summary')} isExpanded>
            <div className="summary-children">
                <div className="inputs-wrapper">
                    <TextField
                        id="summary-model-name"
                        label={`${t('ra:evaluationModelName')}*`}
                        fullWidth
                        value={modelName}
                        disabled={isEvaluationModelUsed}
                        onChange={onChange('name')}
                        size="small"
                        placeholder={t('ra:enterModelName')}
                        helperText={
                            <CharactersRemaining
                                id="summary-detail-template-name"
                                length={state.name.length}
                                max={70}
                                error={errors.name || nameExistError}
                            />
                        }
                        variant="outlined"
                        color="secondary"
                        error={!!errors.name || !!nameExistError}
                        className="mb mr"
                    />
                    <div className="checkboxes-wrapper mt gap-10">
                        <Checkbox
                            rounded={null}
                            className="cell-checkbox no-full-width flex-start no-border"
                            checked={active}
                            onChange={() => {
                                changeActive(!active);
                            }}
                            label={t('active')}
                            data-testid="active-checkbox"
                            size="medium"
                        />
                        <Checkbox
                            rounded={null}
                            className="cell-checkbox no-full-width flex-start no-border"
                            checked={includeDetectability}
                            disabled={isEvaluationModelUsed}
                            onChange={() => {
                                changeDetectability(!includeDetectability);
                            }}
                            label={t('ra:includeDetectability')}
                            labelClassName={cn({ 'checkbox-label-disabled': isEvaluationModelUsed })}
                            data-testid="include-detectability-checkbox"
                            size="medium"
                        />
                    </div>
                </div>
                <div className="inputs-wrapper">
                    <div className="checkboxes-wrapper">
                        <p className="checkbox-title">
                            {t('ra:likelihoodLabel')} {t('ra:valuesToDisplay')}
                        </p>
                        <div className="checkboxes">
                            {arrayLikelihood.map((item: ILabelItem, index: number) => (
                                <Checkbox
                                    rounded={null}
                                    key={index}
                                    className="cell-checkbox no-full-width flex-start no-border"
                                    checked={item.selected}
                                    disabled={item.disabled || isEvaluationModelUsed}
                                    label={item.label}
                                    onChange={() => {
                                        const array = [...arrayLikelihood];
                                        array[index] = {
                                            ...array[index],
                                            selected: !item.selected,
                                        };
                                        onDisplayValuesChange('likelihood', index);
                                        setArrayLikelihood(array);
                                    }}
                                    labelClassName={cn({
                                        'checkbox-label-disabled': item.disabled || isEvaluationModelUsed,
                                    })}
                                    data-testid={`likelihood-checkbox-${item.label}`}
                                    size="medium"
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className="inputs-wrapper">
                    <div className="checkboxes-wrapper">
                        <p className="checkbox-title">
                            {t('ra:impact')} {t('ra:valuesToDisplay')}
                        </p>
                        <div className="checkboxes">
                            {arrayImpact.map((item: ILabelItem, index: number) => (
                                <Checkbox
                                    rounded={null}
                                    key={index}
                                    className="cell-checkbox no-full-width flex-start no-border"
                                    checked={item.selected}
                                    disabled={item.disabled || isEvaluationModelUsed}
                                    label={item.label}
                                    onChange={() => {
                                        const array = [...arrayImpact];
                                        array[index] = {
                                            ...array[index],
                                            selected: !item.selected,
                                        };
                                        onDisplayValuesChange('impact', index);
                                        setArrayImpact(array);
                                    }}
                                    labelClassName={cn({
                                        'checkbox-label-disabled': item.disabled || isEvaluationModelUsed,
                                    })}
                                    data-testid={`impact-checkbox-${item.label}`}
                                    size="medium"
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className="inputs-wrapper">
                    <div className="checkboxes-wrapper">
                        <p className="checkbox-title">
                            {t('ra:detectability')} {t('ra:valuesToDisplay')}
                        </p>
                        <div className="checkboxes">
                            {arrayDetectability.map((item: ILabelItem, index: number) => (
                                <Checkbox
                                    rounded={null}
                                    key={index}
                                    className="cell-checkbox no-full-width flex-start no-border"
                                    checked={item.selected}
                                    disabled={item.disabled || !includeDetectability || isEvaluationModelUsed}
                                    label={item.label}
                                    onChange={() => {
                                        const array = [...arrayDetectability];
                                        array[index] = {
                                            ...array[index],
                                            selected: !item.selected,
                                        };
                                        onDisplayValuesChange('detectability', index);
                                        setArrayDetectability(array);
                                    }}
                                    labelClassName={cn({
                                        'checkbox-label-disabled':
                                            item.disabled || !includeDetectability || isEvaluationModelUsed,
                                    })}
                                    data-testid={`detectability-checkbox-${item.label}`}
                                    size="medium"
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </ExpandableItem>
    );
};

export default SummaryDetailTemplate;
